import { subject } from "@casl/ability";
import axios, { AxiosRequestConfig } from "axios";
import isNil from "lodash/isNil";

import getErrorDetails from "api/helpers/getErrorDetails";
import {
  addPaginationAndSort,
  getPaginationInfoFromHeaders,
  ORDER_DESC,
  PaginatedList,
} from "api/helpers/pagination";
import paramsSerializer from "api/helpers/paramsSerializer";
import { portalApi, TAGS } from "api/rtkApi";
import { DEFAULT_PAGINATION } from "constants/defaultPagination";
import { SUBJECT_USER } from "models/role/subject";
import {
  PaginationParams,
  preparePaginationParams,
} from "utils/preparePaginationParams";

import type { RTKMeta } from "models/RTKMeta";
import type { UUID } from "utils";
import type { OrderableColumns } from "../constants/User";
import type { BasicUser, default as User, UserPatch } from "../models/User";
import type { UserForm } from "../models/UserForm";

export async function getUsers(
  qsParams: { customerId?: UUID | UUID[] },
  options: AxiosRequestConfig
): Promise<PaginatedList<User>> {
  const { customerId } = qsParams;
  const params: Record<string, any> = {
    ...addPaginationAndSort<User>({
      orderBy: "createdAt",
      order: ORDER_DESC,
      limit: DEFAULT_PAGINATION.limit,
      page: 0,
    }),
  };

  if (!isNil(customerId)) {
    params["customer"] = customerId;
  }

  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/users",
      params,
      paramsSerializer,
    });

    const { data = {}, headers: responseHeaders = {} } = response;

    const {
      totalItems,
      currentPage,
      currentLimit,
    } = getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: (data ?? []).map((user: User) => subject(SUBJECT_USER, user)),
      totalItems,
      currentPage,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function postUser(user: UserForm, options: any): Promise<UUID> {
  try {
    const { data }: { data: User } = await axios({
      ...options,
      url: "/users",
      method: "POST",
      data: user,
    });

    return data.id;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchUser(
  id: UUID,
  user: Partial<UserPatch>,
  options: any
): Promise<UUID> {
  try {
    const { data }: { data: User } = await axios({
      ...options,
      method: "PUT",
      url: `/users/${id}`,
      data: user,
    });

    return data.id;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

interface SendProductInterestData {
  productName: string;
}

export async function sendProductInterest(
  data: SendProductInterestData,
  options: AxiosRequestConfig
) {
  try {
    await axios({
      ...options,
      method: "POST",
      url: "/users/settings/product-interest",
      data,
    });
  } catch (error) {
    throw getErrorDetails(error);
  }
}

type GetUsersResponse = {
  users: User[];
  totalItems: number;
};

type GetUsersQuery = {
  customer?: UUID | UUID[];
} & PaginationParams<OrderableColumns>;

type GetUsernamesQuery = {
  userIds?: UUID | UUID[];
};

export type GetUsernamesResponse = Record<UUID, string>;

export const userExtendedApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<GetUsersResponse, GetUsersQuery>({
      query: ({ customer, ...rest }) => ({
        url: "users",
        params: {
          customer,
          ...preparePaginationParams(rest),
        },
      }),
      providesTags: [TAGS.USERS],
      transformResponse: (users: User[], meta: RTKMeta) => ({
        users,
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    getUser: build.query<User, UUID>({
      query: (userId) => `users/${userId}`,
      transformResponse: (user: User) => subject(SUBJECT_USER, user),
      providesTags: (_, __, id) => [{ type: TAGS.USERS, id }],
    }),
    getUsernames: build.query<GetUsernamesResponse, GetUsernamesQuery>({
      query: ({ userIds }) => ({
        url: "users",
        method: "GET",
        params: {
          id: userIds,
          ...DEFAULT_PAGINATION,
        },
      }),
      transformResponse: (data: BasicUser[]) =>
        data.reduce<GetUsernamesResponse>(
          (names, user) => ({
            ...names,
            [user.id]: user.name,
          }),
          {}
        ),
      providesTags: [TAGS.USER_NAMES],
    }),
    resetPassword: build.mutation<UUID, UUID>({
      query: (id) => ({
        url: `/users/${id}/password/reset`,
        method: "POST",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userExtendedApi.util.updateQueryData(
            "getUser",
            id,
            (cachedUser: User) => {
              cachedUser.locked = false;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateUser: build.mutation<
      string,
      { id: UUID; user: Partial<UserPatch>; options?: Record<string, unknown> }
    >({
      query: ({ id, user }) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: user,
      }),
      async onQueryStarted({ id, user }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userExtendedApi.util.updateQueryData(
            "getUser",
            id,
            (cachedUser: User) => {
              Object.entries(user).reduce((accumulator, [key, value]) => {
                (accumulator as any)[key] = value;

                return accumulator;
              }, cachedUser);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [TAGS.USERS],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserQuery,
  useGetUsersQuery,
  useGetUsernamesQuery,
  useResetPasswordMutation,
  useUpdateUserMutation,
} = userExtendedApi;

export const { getUser } = userExtendedApi.endpoints;
